import React from "react"
import { Link } from "react-router-dom"
import Footer from "../components/footer"
import Navbar from "../components/nav"

function AiSmartGalleryPolicy() {
  return (
    <>
      <header className="header-area">
        <div className="navbar-area">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <Navbar />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div id="home">
        <div className="container mt-125 mb-50">
          <div className="card-body shadow">
            <h1>Privacy Policy</h1>
            <h4 className="mt-3">AI Smart Gallery: Photo Search</h4>
            <p className="mt-2"><strong>Last Updated: September 25, 2026</strong></p>
            <p><strong>Developer:</strong> MWX Apps</p>
            <p><strong>App Name:</strong> AI Smart Gallery: Photo Search</p>
            <p><strong>Package Name:</strong> mwxapps.smart.gallery</p>
            <p><strong>Contact Email:</strong> web-developer@outlook.com</p>

            <hr className="my-4" />

            {/* Introduction */}
            <h4 className="mt-4" id="introduction">Introduction</h4>
            <p className="mt-3">
              Welcome to AI Smart Gallery ("we," "our," or "us"). AI Smart Gallery
              is a photo gallery whose AI features run <strong>entirely on your
              device</strong>. This Privacy Policy explains what the app accesses,
              what stays on your device, and what can leave it.
            </p>
            <p className="mt-3">
              <strong className="text-primary">
                Our Core Privacy Principle: Your photos, your phone, your AI &amp;
                nobody else's. There is no account, no analytics and no
                crash-reporting SDK, and your photos never leave your device. We do
                not sell or share personal data.
              </strong>
            </p>

            <hr className="my-4" />

            {/* Data We Access */}
            <h4 className="mt-4" id="access">What the App Accesses</h4>
            <div className="table-responsive mt-3">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Why</th>
                    <th>Where it goes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Photos and videos you allow the app to read</td>
                    <td>Show your gallery, build the on-device AI index (search, faces, captions, object tags), smart albums, cleaner</td>
                    <td>Stays on your device</td>
                  </tr>
                  <tr>
                    <td>Photo location metadata (EXIF GPS)</td>
                    <td>Map and place grouping</td>
                    <td>Stays on your device, except for place-name lookups (see "When Data Leaves Your Device")</td>
                  </tr>
                  <tr>
                    <td>Changes you approve (move, rename, delete, trash)</td>
                    <td>File management; Android asks you to confirm each change unless you turn on "Media management"</td>
                    <td>On your device</td>
                  </tr>
                  <tr>
                    <td>Vault and hidden photos</td>
                    <td>Kept in the app's private storage; vault items are encrypted (AES-256) with a key derived from your PIN or password</td>
                    <td>On your device only. They are deleted if you uninstall the app or clear its data</td>
                  </tr>
                  <tr>
                    <td>Fingerprint (optional)</td>
                    <td>Unlock the vault</td>
                    <td>Handled by Android; the app never receives biometric data</td>
                  </tr>
                  <tr>
                    <td>Microphone (only when you tap the microphone)</td>
                    <td>Voice search through Android's speech recognizer</td>
                    <td>Processed by the speech service installed on your phone</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr className="my-4" />

            {/* On-device AI index */}
            <h4 className="mt-4" id="ai-index">The On-Device AI Index</h4>
            <p className="mt-3">
              Search vectors, face groups, captions and labels are computed on your
              device and stored in the app's private storage. They are excluded from
              Android cloud backups and are deleted with "Remove index" in the app
              or when the app is uninstalled.
            </p>

            <hr className="my-4" />

            {/* Data leaving device */}
            <h4 className="mt-4" id="leaving">When Data Leaves Your Device</h4>
            <ul className="mt-2">
              <li><strong>Cloud storage you connect yourself (e.g. Dropbox).</strong> When you sign in to a provider, the app talks directly to that provider to list, upload, download, move or delete the files you choose. Sign-in tokens are stored encrypted on your device and are revoked when you sign out. The provider's own privacy policy applies to files stored there.</li>
              <li><strong>AI model download.</strong> The AI models are delivered by Google Play. No photo or personal data is sent in this process.</li>
              <li><strong>Place names.</strong> To show a readable place name, the app may pass a photo's coordinates to Android's built-in geocoder service, which can use the network. When it is unavailable, an offline place list bundled with the app is used.</li>
              <li><strong>Sharing and "Open in Maps".</strong> Only when you choose to, Android hands the selected content to the app you pick.</li>
            </ul>

            <hr className="my-4" />

            {/* Advertising */}
            <h4 className="mt-4" id="advertising">Advertising (Google AdMob)</h4>
            <p className="mt-3">
              Advertising is being introduced in an upcoming version of the App.
              Once it is enabled, AI Smart Gallery will remain free to use and will
              be supported by ads served through <strong>Google AdMob</strong>. To
              serve and measure those ads, the Google Mobile Ads SDK may collect
              and process:
            </p>
            <ul className="mt-2">
              <li><strong>Advertising ID (AAID)</strong> — the resettable Android advertising identifier, used for ad frequency capping and, where you consent, personalisation</li>
              <li><strong>Device and app information</strong> — device model, operating-system version, app version, language and coarse (IP-based) location</li>
              <li><strong>Ad interaction data</strong> — which ads were shown, viewed or tapped, used for reporting and fraud prevention</li>
            </ul>
            <p className="mt-2">
              Where the law requires it (for example in the EEA and the UK), we ask
              for your consent before personalised advertising through Google's User
              Messaging Platform, and you can change or withdraw that choice at any
              time from the app's settings. You can also reset or delete your
              Advertising ID in <em>Android Settings → Google → Ads</em>. Google's
              handling of this data is governed by{" "}
              <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google's Advertising Policies</a>.
            </p>
            <p className="mt-2">
              <strong>Note:</strong> Advertising does not change how the AI works —
              your photos, the AI index and the vault always stay on your device. We
              never share your photos with advertising partners.
            </p>

            <hr className="my-4" />

            {/* Permissions */}
            <h4 className="mt-4" id="permissions">Permissions</h4>
            <ul className="mt-2">
              <li>Photos and videos (or only selected photos)</li>
              <li>Media location</li>
              <li>Notifications (indexing and transfer progress)</li>
              <li>Internet and network state (cloud and model download)</li>
              <li>Biometric (vault unlock)</li>
              <li>On Android 12 and later, optional "Media management" so approved changes run without a confirmation each time</li>
              <li><strong>com.google.android.gms.permission.AD_ID</strong> and <strong>ACCESS_NETWORK_STATE</strong> — added with the AdMob advertising feature described above (Android 13 and above). You can reset or delete the Advertising ID in Android Settings → Google → Ads</li>
            </ul>
            <p className="mt-2">
              The app does <strong>not</strong> request "All files access".
            </p>

            <hr className="my-4" />

            {/* Third-party services */}
            <h4 className="mt-4" id="third-party">Third-Party Services</h4>

            <h5 className="mt-4">Google AdMob (Advertising)</h5>
            <ul className="mt-2">
              <li><strong>Purpose:</strong> Will serve the ads shown in the App once advertising is enabled</li>
              <li><strong>Data Processed:</strong> Advertising ID, device and app information, coarse location, ad interactions</li>
              <li><strong>Privacy:</strong> See <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google's Advertising Policies</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a></li>
              <li><strong>We do not control</strong> Google or the ads it serves</li>
            </ul>

            <h5 className="mt-4">Cloud Storage You Connect (e.g. Dropbox)</h5>
            <ul className="mt-2">
              <li>Optional. The app talks directly to the provider you choose, and that provider's own terms and privacy policy apply to files stored there</li>
              <li><strong>We do not control</strong> these providers and receive no copy of your files</li>
            </ul>

            <h5 className="mt-4">Google Play</h5>
            <p className="mt-2">
              The App is distributed through Google Play and the on-device AI models are
              delivered by it. Google Play has its own privacy policy. Please refer to{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>{" "}
              for information about how Google handles data related to app distribution.
            </p>

            <h5 className="mt-4">We Do NOT Use:</h5>
            <ul className="mt-2">
              <li>Google Analytics or Firebase Analytics</li>
              <li>Facebook SDK or social media tracking SDKs</li>
              <li>Advertising networks other than Google AdMob (see "Advertising" above)</li>
              <li>Crash reporting that collects personal identifiers</li>
              <li>Any third-party tracking services other than the Google Mobile Ads SDK</li>
            </ul>

            <hr className="my-4" />

            {/* What we do NOT collect */}
            <h4 className="mt-4" id="not-collected">Information We Do NOT Collect</h4>
            <ul className="mt-2" style={{ listStyle: "none", paddingLeft: "10px" }}>
              <li>❌ We do NOT create an account or collect your email, name or password</li>
              <li>❌ We do NOT use Firebase Analytics, Google Analytics, or any behavioural tracking SDK</li>
              <li>❌ We do NOT use crash-reporting services that collect personal identifiers</li>
              <li>❌ We do NOT sell or share your personal data with third parties</li>
              <li>❌ We do NOT upload your photos to our servers — the AI runs on your device</li>
            </ul>

            <hr className="my-4" />

            {/* Data security */}
            <h4 className="mt-4" id="security">Data Security</h4>
            <ul className="mt-2">
              <li>Your photos, the AI index and the vault never leave your device. Vault items are encrypted with AES-256 using a key derived from your PIN or password</li>
              <li>All network traffic uses HTTPS. Cleartext traffic is disabled in the app</li>
              <li>Cloud sign-in tokens are stored in Android Keystore-backed encrypted storage and are revoked when you sign out</li>
              <li>The app requests no account and stores no password on any server</li>
            </ul>

            <hr className="my-4" />

            {/* Children */}
            <h4 className="mt-4" id="childrens-privacy">Children's Privacy</h4>
            <p className="mt-3">
              The app is not directed at children under 13 and does not knowingly
              collect their data. If you are a parent and believe your child has
              provided personal information to us, please contact us at
              web-developer@outlook.com and we will assist.
            </p>

            <hr className="my-4" />

            {/* Data retention */}
            <h4 className="mt-4" id="local-data">Data Retention and Deletion</h4>
            <p className="mt-3">
              Because the app has no account, the developer keeps no personal data about you.
            </p>
            <ul className="mt-2">
              <li><strong>Remove the AI index in the app:</strong> Settings → "Remove index" deletes the search vectors, face groups, captions and labels built on your device</li>
              <li><strong>Delete local data:</strong> clearing the app's storage in Android Settings → Apps → AI Smart Gallery → Storage → Clear Data, or uninstalling the app, removes the AI index, vault, hidden items, settings and cached data</li>
              <li><strong>By email:</strong> you can also contact us at web-developer@outlook.com with any deletion request</li>
            </ul>

            <hr className="my-4" />

            {/* Your rights / choices */}
            <h4 className="mt-4" id="your-rights">Your Rights and Choices</h4>
            <p className="mt-3">
              Depending on where you live, you may have the right to access, correct,
              export or erase your personal data, and to object to processing. Because
              the app processes everything on your device and keeps no account, these
              controls live in the app and in Android settings.
            </p>
            <ul className="mt-2">
              <li>You can limit photo access in Android settings</li>
              <li>You can remove the AI index in the app ("Remove index")</li>
              <li>You can sign out of cloud providers (which also deletes their cached AI vectors)</li>
              <li>You can reset or delete the Advertising ID in Android Settings → Google → Ads, and change your ad-consent choice from the app's settings</li>
              <li>You can uninstall the app to delete all app data on the device</li>
              <li>You can request deletion or ask any question by writing to web-developer@outlook.com</li>
            </ul>

            <hr className="my-4" />

            {/* International users */}
            <h4 className="mt-4" id="international">International Users</h4>
            <p className="mt-3">
              AI Smart Gallery can be used anywhere in the world. The AI features and
              your photos, AI index and vault stay on your device and are not transferred
              to us. If advertising is enabled, data processed by Google AdMob (such as the
              Advertising ID) may be transferred to and processed in countries other than
              your own, as described in Google's own privacy policy.
            </p>

            <hr className="my-4" />

            {/* Changes */}
            <h4 className="mt-4" id="changes">Changes to This Privacy Policy</h4>
            <p className="mt-3">
              Updates to this policy are published at the same address with a new
              date. You are advised to review this page periodically for any changes.
              These changes are effective immediately after they are posted.
            </p>

            <hr className="my-4" />

            {/* Contact */}
            <h4 className="mt-4" id="contact">Contact Us</h4>
            <p className="mt-3">
              If you have any questions or suggestions about this Privacy Policy, do
              not hesitate to contact us at: web-developer@outlook.com
            </p>
            <p className="mt-2"><strong>Developer:</strong> MWX Apps</p>
            <p><strong>App:</strong> AI Smart Gallery: Photo Search (mwxapps.smart.gallery)</p>
            <p><strong>Website:</strong> https://mwx-apps.web.app/AiSmartGallery</p>

            <hr className="my-4" />

            {/* Summary */}
            <h4 className="mt-4" id="summary">Summary</h4>
            <div className="table-responsive mt-3">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Collected?</th>
                    <th>Shared?</th>
                    <th>Stored where</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Photos and videos</td>
                    <td>❌ Not by us</td>
                    <td>❌ No</td>
                    <td>Your device only</td>
                  </tr>
                  <tr>
                    <td>On-device AI index (vectors, faces, captions, labels)</td>
                    <td>❌ Not by us</td>
                    <td>❌ No</td>
                    <td>Your device only (app private storage)</td>
                  </tr>
                  <tr>
                    <td>Photo location metadata (EXIF GPS)</td>
                    <td>❌ Not by us</td>
                    <td>❌ No</td>
                    <td>Your device only (network geocoder for place names)</td>
                  </tr>
                  <tr>
                    <td>Vault / hidden photos</td>
                    <td>❌ Not by us</td>
                    <td>❌ No</td>
                    <td>Your device only (AES-256 encrypted)</td>
                  </tr>
                  <tr>
                    <td>Biometric data</td>
                    <td>❌ Not by us</td>
                    <td>❌ No</td>
                    <td>Handled by Android</td>
                  </tr>
                  <tr>
                    <td>Email address / account</td>
                    <td>❌ No account</td>
                    <td>❌ No</td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td>Advertising ID (AAID)</td>
                    <td>✅ Yes — via Google AdMob (upcoming version)</td>
                    <td>✅ Processed by Google</td>
                    <td>Google (AdMob)</td>
                  </tr>
                  <tr>
                    <td>Analytics / usage</td>
                    <td>❌ No</td>
                    <td>❌ No</td>
                    <td>—</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4">
              <h5 className="text-primary">AI Smart Gallery: Photo Search</h5>
              <p className="mt-2"><em>This privacy policy is effective as of September 25, 2026.</em></p>
              <p className="mt-1">
                <Link to="/AiSmartGallery/terms-and-conditions" style={{ color: "#0898e7", fontWeight: "600" }}>
                  Terms &amp; Conditions
                </Link>
              </p>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  )
}

export default AiSmartGalleryPolicy